import * as React from "react";

import { AccessSummaryEditChartColorsElementEnum } from "@app/access-reports/admin";
import { AccessReportsWizardElement, AccessReportsWizardElementTypeEnum, ByProductDefault } from "@app/access-reports/shared";
import { QESButton, QESTextInput } from "@app/shared/qes-standards";
import { cloneDeep, isEqual } from "lodash";
import { ColorResult, SketchPicker } from "react-color";
import { FlyoutFooter } from "@app/shared/Flyouts";

interface AccessSummaryEditChartColorsProps {
  byProductDefault: ByProductDefault;
  disableButtons: boolean;
  elements: AccessReportsWizardElement[];
  hideSidePanel: () => void;
  onSaveAccessSummaryChartColorsClick: (byProductDefault: ByProductDefault) => void;
}

interface AccessSummaryEditChartColorsState {
  clonedByProductDefault: ByProductDefault;
  displayColorPicker: AccessSummaryEditChartColorsElementEnum;
}

class AccessSummaryEditChartColors extends React.Component<AccessSummaryEditChartColorsProps, AccessSummaryEditChartColorsState> {
  constructor(props: AccessSummaryEditChartColorsProps) {
    super(props);

    this.state = {
      clonedByProductDefault: null,
      displayColorPicker: null,
    };
  }

  public componentDidMount() {
    this.extractChartColorDefaults();
  }

  public componentDidUpdate(prevProps: AccessSummaryEditChartColorsProps) {
    if (!isEqual(prevProps.elements, this.props.elements)) {
      this.extractChartColorDefaults();
    }
  }

  public render() {
    return (
      <React.Fragment>
        <div className="row align-items-center mb-3">
          {this.buildAccessSummaryElement(AccessSummaryEditChartColorsElementEnum.ChartPercentage1)}
          {this.buildAccessSummaryElement(AccessSummaryEditChartColorsElementEnum.ChartPercentage1Color)}
        </div>

        <div className="row align-items-center mb-3">
          {this.buildAccessSummaryElement(AccessSummaryEditChartColorsElementEnum.ChartPercentage2)}
          {this.buildAccessSummaryElement(AccessSummaryEditChartColorsElementEnum.ChartPercentage2Color)}
        </div>

        <div className="row align-items-center mb-3">
          {this.buildAccessSummaryElement(AccessSummaryEditChartColorsElementEnum.ChartPercentage3)}
          {this.buildAccessSummaryElement(AccessSummaryEditChartColorsElementEnum.ChartPercentage3Color)}
        </div>

        <div className="row align-items-center mb-3">
          <div className="offset-md-2 col-md-4">
            <label className="label-s mb-0">
              Less than {this.getValue(AccessSummaryEditChartColorsElementEnum.ChartPercentage3)}% With Access
            </label>
          </div>
          {this.buildAccessSummaryElement(AccessSummaryEditChartColorsElementEnum.ChartPercentage4Color)}
        </div>

        <FlyoutFooter
          submitButton={
            <QESButton
              disabled={this.disableSaveButton()}
              onClick={() => this.props.onSaveAccessSummaryChartColorsClick(this.state.clonedByProductDefault)}
              qesTestId="access-reports-admin__access-summary-chart-colors--save"
              qesType="primary"
            >
              Save
            </QESButton>
          }
        />
      </React.Fragment>
    );
  }

  private buildAccessSummaryElement = (elementId: AccessSummaryEditChartColorsElementEnum): React.ReactNode => {
    const element: AccessReportsWizardElement = this.props.elements.find(x => x.elementId === elementId);
    const value: string = this.getValue(elementId);

    if (!!element) {
      switch (element.elementType) {
        case AccessReportsWizardElementTypeEnum.ColorPicker:
          const presetColors: string[] = element.options.map(x => x.color as string);

          return (
            <React.Fragment>
              <div className="col-md-6 d-flex align-items-center">
                <div className="color-picker-swatch" onClick={() => this.onColorPickerClick(element.elementId as AccessSummaryEditChartColorsElementEnum)}>
                  <div className="color-picker-color" style={{ backgroundColor: value || "#000" }} />
                </div>

                {(this.state.displayColorPicker === element.elementId) &&
                  <div className="color-picker-popover">
                    <div
                      className="color-picker-cover"
                      onClick={() => this.onColorPickerClose()}
                    />

                    <SketchPicker
                      color={value || "#000"}
                      disableAlpha={true}
                      presetColors={presetColors}
                      onChange={this.onColorPickerChange}
                    />
                  </div>
                }
              </div>
            </React.Fragment>
          );
        case AccessReportsWizardElementTypeEnum.Input:
          return (
            <React.Fragment>
              <div className="col-md-2">
                <QESTextInput
                  testId={`access-reports-admin__access-summary-chart-colors--${element.elementId}-input`}
                  disabled={this.props.disableButtons}
                  isValid={this.getInputValidity(element.elementId as AccessSummaryEditChartColorsElementEnum, value)}
                  label=""
                  onChange={(e) => this.onInputChange(element.elementId as AccessSummaryEditChartColorsElementEnum, e)}
                  unrequiredValidation={true}
                  value={value}
                />
              </div>
              <div className="col-md-4">
                <label className="label-s mb-0">
                  % Or Greater With Access
                </label>
              </div>
            </React.Fragment>
          );
        default:
          console.error("Invalid Access Reports Summary Element Type");
          break;
      }
    }

    return (
      <div className="form-group mb-3">
        <i className="text-danger fa fa-fw fa-exclamation-circle" />
        <span className="text-muted">
          Error
        </span>
      </div>
    );
  }

  private disableSaveButton = (): boolean => {
    if (this.props.disableButtons || isEqual(this.props.byProductDefault, this.state.clonedByProductDefault) || !this.state.clonedByProductDefault) {
      return true;
    }

    for (const bpd of Object.keys(this.state.clonedByProductDefault)) {
      if (this.state.clonedByProductDefault[bpd] === null || this.state.clonedByProductDefault[bpd] === undefined) {
        return true;
      }

      if (bpd === AccessSummaryEditChartColorsElementEnum.ChartPercentage1 ||
        bpd === AccessSummaryEditChartColorsElementEnum.ChartPercentage2 ||
        bpd === AccessSummaryEditChartColorsElementEnum.ChartPercentage3
      ) {
        const inputValidity: boolean = this.getInputValidity(bpd, this.state.clonedByProductDefault[bpd].toString());

        if (!inputValidity) {
          return true;
        }
      }
    }

    return false;
  }

  private extractChartColorDefaults = (): void => {
    if (!!this.props.byProductDefault) {
      const clonedByProductDefault: ByProductDefault = cloneDeep(this.props.byProductDefault);

      this.setState({
        clonedByProductDefault,
      });
    }
  }

  private getInputValidity = (elementId: AccessSummaryEditChartColorsElementEnum, value: string): boolean => {
    if (!value) {
      return false;
    }

    const chartPercentageOneValue = parseInt(this.getValue(AccessSummaryEditChartColorsElementEnum.ChartPercentage1));
    const chartPercentageTwoValue = parseInt(this.getValue(AccessSummaryEditChartColorsElementEnum.ChartPercentage2));
    const chartPercentageThreeValue = parseInt(this.getValue(AccessSummaryEditChartColorsElementEnum.ChartPercentage3));

    const parsedValue: number = parseInt(value);

    if (elementId === AccessSummaryEditChartColorsElementEnum.ChartPercentage1) {
      if (parsedValue <= chartPercentageTwoValue || parsedValue <= chartPercentageThreeValue) {
        return false;
      }
    } else if (elementId === AccessSummaryEditChartColorsElementEnum.ChartPercentage2) {
      if (parsedValue >= chartPercentageOneValue || parsedValue <= chartPercentageThreeValue) {
        return false;
      }
    } else if (elementId === AccessSummaryEditChartColorsElementEnum.ChartPercentage3) {
      if (parsedValue >= chartPercentageOneValue || parsedValue >= chartPercentageTwoValue) {
        return false;
      }
    }

    return true;
  }

  private getValue = (elementId: AccessSummaryEditChartColorsElementEnum): string => {
    if (!!this.state.clonedByProductDefault) {
      return this.state.clonedByProductDefault[elementId] as string;
    }

    return "";
  }

  private onInputChange = (elementId: AccessSummaryEditChartColorsElementEnum, event: React.ChangeEvent<HTMLInputElement>): void => {
    const value: number = parseInt(event.target.value);

    if ((!isNaN(value) && value > 0 && value < 100) || !event.target.value) {
      const clonedByProductDefault: ByProductDefault = this.state.clonedByProductDefault;
      clonedByProductDefault[elementId] = !event.target.value ? event.target.value : value;

      this.setState({
        clonedByProductDefault,
      });
    }
  }

  private onColorPickerClick = (accessSummaryElementId: AccessSummaryEditChartColorsElementEnum): void => {
    if (accessSummaryElementId === this.state.displayColorPicker) {
      this.setState({
        displayColorPicker: null,
      });
    } else {
      this.setState({
        displayColorPicker: accessSummaryElementId,
      });
    }
  }

  private onColorPickerChange = (color: ColorResult): void => {
    const clonedByProductDefault: ByProductDefault = this.state.clonedByProductDefault;
    clonedByProductDefault[this.state.displayColorPicker] = color.hex;

    this.setState({
      clonedByProductDefault,
    });
  }

  private onColorPickerClose = (): void => {
    this.setState({
      displayColorPicker: null,
    });
  }
}

export { AccessSummaryEditChartColors };
